import React from 'react';

// =============================================================================
// AdditionalCostPanel
// =============================================================================
// The Accounting workspace's Additional Cost grid (legacy dash_CSR_AddCost,
// simplified to a single "No Pay Status" bucket). Two value columns: the count of
// additional-cost records and their total cost ($). Fed by
// /lc-dashboards/additional-cost-counts. The row is clickable — it drills the
// additional-cost-level records into the standard results area (onSelect).
// Purely presentational; the page owns data fetching + drill wiring.

export interface AdditionalCostData {
  records: number;
  cost: number;
}

export interface AdditionalCostPanelProps {
  data: AdditionalCostData | null;
  loading: boolean;
  onSelect: () => void;
}

const AdditionalCostPanel: React.FC<AdditionalCostPanelProps> = ({ data, loading, onSelect }) => {
  const recs = data === null ? (loading ? '…' : '0') : data.records.toLocaleString('en-US');
  const cost = data === null
    ? (loading ? '…' : '$0.00')
    : '$' + data.cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    // w-full so the table fills the tile width (header + columns stretch to the panel edges);
    // natural height keeps the tile's double-click fit-to-content working.
    <div className="w-full bg-panel text-sm">
      <table className="border-collapse w-full">
        <thead>
          <tr className="bg-slate-700 text-white">
            <th className="px-2 py-1 font-semibold text-left min-w-[150px]">Additional Cost</th>
            <th className="px-2 py-1 font-semibold text-right border-l border-slate-500 min-w-[64px]">Records</th>
            <th className="px-2 py-1 font-semibold text-right border-l border-slate-500 min-w-[80px]">Cost</th>
          </tr>
        </thead>
        <tbody className="text-body">
          <tr
            onClick={onSelect}
            title="View No Pay Status"
            className="cursor-pointer border-t border-gray-100 dark:border-gray-700/50 hover:bg-blue-50 dark:hover:bg-slate-700/40 transition-colors"
          >
            <td className="px-2 py-1 whitespace-nowrap" style={{ paddingLeft: '8px' }}>No Pay Status</td>
            <td className="px-2 py-1 text-right tabular-nums border-l border-gray-100 dark:border-gray-700/50">
              <span className="font-semibold">{recs}</span>
            </td>
            <td className="px-2 py-1 text-right tabular-nums border-l border-gray-100 dark:border-gray-700/50">
              <span className="font-semibold">{cost}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default AdditionalCostPanel;
